import { RemoteResult, RemoteResultConfiguration } from '../../index.types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { useImageEvaluation } from '../../useImageEvaluation';
import Icon from '../../../../../components/envago/Icon/Icon';
import { useTranslation } from 'react-i18next';

import './RemoteResultCamera.styles.less';
import { CameraOverlay } from './components/CameraOverlay';
import { RoundIconButton } from '../../../../../components/envago/Button';
import { ImagePicker } from '../../../../../components/envago/ImagePicker';
import { ImagePickerHandle } from '../../../../../components/envago/ImagePicker/ImagePicker';
import { AI_CAMERA_SCREENSHOT_QUALITY, AI_IMAGE_MAX_DIMENSION, SUPPORTED_READING_IMAGE_ACCEPT } from '../../../../../helper/ImageFileHelper';

interface RemoteResultCameraProps {
    config: RemoteResultConfiguration;
    onRemoteResultReceived: (result: RemoteResult | undefined, imageBase64: string) => void;
    onClose: () => void;
    allowGallery?: boolean;
    onGalleryImageSelected?: (imageBase64: string) => Promise<void> | void;
    aiEnabled?: boolean;
}

const RemoteResultCamera = ({ config, onRemoteResultReceived, onClose, allowGallery = false, onGalleryImageSelected, aiEnabled = true }: RemoteResultCameraProps) => {
    const webcamRef = useRef<Webcam>(null);
    const galleryPickerRef = useRef<ImagePickerHandle>(null);
    const isMountedRef = useRef(true);
    const { t } = useTranslation();
    const { evaluate } = useImageEvaluation(config);

    const [preview, setPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [isGalleryPickerOpen, setIsGalleryPickerOpen] = useState(false);

    const videoConstraints = {
        facingMode: { ideal: 'environment' },
    };

    useEffect(() => {
        // Set again on every mount: React 18 StrictMode runs mount → cleanup → mount in development.
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const takeImage = useCallback(async () => {
        const shot = webcamRef.current?.getScreenshot();

        if (!shot) {
            return;
        }

        setPreview(shot);

        if (!aiEnabled) {
            onRemoteResultReceived(undefined, shot);
            setPreview(null);
            return;
        }

        setLoading(true);

        const { result, superseded } = await evaluate(shot);

        // A newer shot has taken over — reporting this one would overwrite the newer reading, and its
        // preview belongs to the evaluation still running.
        if (superseded) {
            return;
        }

        onRemoteResultReceived(result, shot);

        if (isMountedRef.current) {
            setLoading(false);
            setPreview(null);
        }
        // `aiEnabled` belongs in this list even though `evaluate` currently changes on every render
        // anyway (its config comes from a JobDetails render-body literal) and therefore masks a
        // missing entry: as soon as that config is memoized, a stale flag would send a photo to the
        // AI although evaluation has just been switched off.
    }, [aiEnabled, evaluate, onRemoteResultReceived]);

    const handleGalleryPick = useCallback(
        async (imageBase64: string) => {
            try {
                await onGalleryImageSelected?.(imageBase64);
            } finally {
                setLoading(false);
            }
        },
        [onGalleryImageSelected],
    );

    const isGalleryBusy = loading || isGalleryPickerOpen;

    useEffect(() => {
        if (!isGalleryPickerOpen) {
            return;
        }

        const reset = () => setIsGalleryPickerOpen(false);

        window.addEventListener('focus', reset);

        // Mobile fallback: native action sheet dismiss doesn't fire window.focus.
        // After 300ms (action sheet animation), listen for the next pointer interaction.
        const timeoutId = window.setTimeout(() => {
            document.addEventListener('pointerdown', reset, { once: true, capture: true });
        }, 300);

        return () => {
            window.removeEventListener('focus', reset);
            window.clearTimeout(timeoutId);
            document.removeEventListener('pointerdown', reset, { capture: true });
        };
    }, [isGalleryPickerOpen]);

    return (
        <div
            className="remote-result-camera"
            onContextMenu={(e) => e.preventDefault()}
        >
            <Webcam
                ref={webcamRef}
                forceScreenshotSourceSize={false}
                audio={false}
                minScreenshotWidth={AI_IMAGE_MAX_DIMENSION}
                screenshotQuality={AI_CAMERA_SCREENSHOT_QUALITY}
                screenshotFormat={'image/jpeg'}
                videoConstraints={videoConstraints}
                style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    objectFit: 'cover',
                    pointerEvents: 'none',
                    visibility: preview ? 'hidden' : 'visible',
                }}
            />
            {preview && (
                <img
                    src={preview}
                    alt={'Preview'}
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        objectFit: 'cover',
                        pointerEvents: 'none',
                    }}
                />
            )}
            <CameraOverlay loading={loading} loadingText={t('ai.cameraDialogDetails.loading')} callToActionText={t('ai.cameraDialogDetails.hint')} />

            {allowGallery && onGalleryImageSelected && (
                <ImagePicker
                    ref={galleryPickerRef}
                    capture={undefined}
                    accept={SUPPORTED_READING_IMAGE_ACCEPT}
                    onOpenChange={setIsGalleryPickerOpen}
                    onChange={handleGalleryPick}
                    onDecodeStart={() => {
                        if (!isMountedRef.current) return;
                        setIsGalleryPickerOpen(false);
                        setLoading(true);
                    }}
                    onDecodeFailed={() => {
                        if (!isMountedRef.current) return;
                        setLoading(false);
                    }}
                />
            )}

            <div className={'absolute left-0 right-0 bottom-2'}>
                <div className={'flex justify-between px-2'}>
                    <div className={'flex flex-1 justify-center items-center'}>
                        <RoundIconButton
                            size={48}
                            secondary
                            ghost
                            // Camera-overlay control: the icon must stay white on the video feed.
                            // Previously this came (accidentally) from the secondary contrast
                            // filter, which no longer applies to ghost buttons.
                            className={'text-white'}
                            icon={<Icon path={Icon.Path.mdiClose} size={'28px'} />}
                            onClick={() => onClose()}
                            disabled={loading}
                        />
                    </div>
                    <div className={'flex flex-1 justify-center items-center'}>
                        <RoundIconButton
                            size={72}
                            primary
                            icon={<Icon path={Icon.Path.mdiCamera} size={'40px'} />}
                            onClick={takeImage}
                            isLoading={loading}
                            disabled={loading}
                        />
                    </div>
                    <div className={'flex flex-1 justify-center items-center'}>
                        {allowGallery && onGalleryImageSelected && (
                            <RoundIconButton
                                size={48}
                                secondary
                                ghost
                                // See the close button above: explicit white for camera overlays.
                                className={'text-white'}
                                icon={<Icon path={Icon.Path.mdiImage} size={'28px'} />}
                                onClick={() => galleryPickerRef.current?.open()}
                                disabled={isGalleryBusy}
                                aria={{ 'aria-label': t('jobs.details.buttons.selectImageFromGallery') }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RemoteResultCamera;
